import {
  createImageEngine,
  DEFAULT_ENHANCEMENTS,
  defaultCorners,
  releaseCanvas,
  type ImageEngine,
} from '@/tools/image-engine';
import { createPdfEngine, openThumbnails, type PdfEngine } from '@/tools/pdf-engine';

/** How long results are kept (the Director, 2026-10-12, #387): the one place the number lives. */
export const DEVICE_TEST_RETENTION_DAYS = 180;

export type TestKind = 'image-engine' | 'pdf-engine';

export type StepName =
  | 'photo-load'
  | 'edge-detection'
  | 'straighten'
  | 'colour-correction'
  | 'pdf-prepare'
  | 'pdf-open'
  | 'pdf-merge'
  | 'pdf-place-image'
  | 'pdf-thumbnails';

/** A step's outcome, as the screen shows it and the API stores it (#489). */
export interface StepResult {
  readonly name: StepName;
  readonly ms: number;
  readonly ok: boolean;
  readonly figures?: Readonly<Record<string, number | boolean>>;
  /** Plain words for the screen; the API keeps at most 200 characters. */
  readonly error?: 'memory' | 'failed';
}

interface StepDef<C> {
  /** `^[a-z0-9-]{1,40}$`, the API's step name. */
  readonly name: StepName;
  run(context: C): Promise<Readonly<Record<string, number | boolean>> | void>;
}

interface TestDef<C> {
  readonly kind: TestKind;
  readonly steps: readonly StepDef<C>[];
  setUp(): C;
  tearDown(context: C): void;
}

/** One test's steps, in order, so the screen can list them before they run. */
export function stepNames(kind: TestKind): readonly StepName[] {
  return (kind === 'image-engine' ? IMAGE_TEST : PDF_TEST).steps.map((step) => step.name);
}

/**
 * Runs a test step by step. A failed step is recorded with its reason and the run goes on: a
 * failure is what the team needs to see (decision 102). `onStep` reports each step as it starts
 * and as it ends.
 */
export async function runTest(
  kind: TestKind,
  onStep: (index: number, result: StepResult | 'running') => void,
): Promise<StepResult[]> {
  return kind === 'image-engine' ? run(IMAGE_TEST, onStep) : run(PDF_TEST, onStep);
}

async function run<C>(
  test: TestDef<C>,
  onStep: (index: number, result: StepResult | 'running') => void,
): Promise<StepResult[]> {
  const context = test.setUp();
  const results: StepResult[] = [];
  try {
    for (const [index, step] of test.steps.entries()) {
      onStep(index, 'running');
      const started = performance.now();
      let result: StepResult;
      try {
        const figures = await step.run(context);
        result = {
          name: step.name,
          ms: Math.round(performance.now() - started),
          ok: true,
          ...(figures ? { figures } : {}),
        };
      } catch (error) {
        result = {
          name: step.name,
          ms: Math.round(performance.now() - started),
          ok: false,
          error: isMemoryError(error) ? 'memory' : 'failed',
        };
      }
      results.push(result);
      onStep(index, result);
    }
  } finally {
    test.tearDown(context);
  }
  return results;
}

function isMemoryError(error: unknown): boolean {
  if (error instanceof RangeError) return true;
  const message = error instanceof Error ? error.message : String(error);
  return /memory|allocat|out of|too large|context lost/i.test(message);
}

// ---- Elaborazione immagini: the Immagini tool's own pipeline on a 12-megapixel photo ----

const PHOTO_WIDTH = 3000;
const PHOTO_HEIGHT = 4000;

interface ImageContext {
  readonly engine: ImageEngine;
  photo?: HTMLCanvasElement;
  corners?: ReturnType<typeof defaultCorners>;
}

const IMAGE_TEST: TestDef<ImageContext> = {
  kind: 'image-engine',
  setUp: () => ({ engine: createImageEngine() }),
  tearDown: (context) => {
    context.engine.dispose();
    releaseCanvas(context.photo);
  },
  steps: [
    {
      name: 'photo-load',
      async run(context) {
        context.photo = drawDocumentPhoto(PHOTO_WIDTH, PHOTO_HEIGHT);
        await context.engine.setSource(context.photo);
        return { megapixels: (PHOTO_WIDTH * PHOTO_HEIGHT) / 1_000_000 };
      },
    },
    {
      name: 'edge-detection',
      async run(context) {
        const found = await context.engine.detectCorners();
        context.corners = found ?? defaultCorners(PHOTO_WIDTH, PHOTO_HEIGHT);
        return { found: found !== null };
      },
    },
    {
      name: 'straighten',
      async run(context) {
        const preview = await context.engine.renderPreview({
          corners: context.corners ?? defaultCorners(PHOTO_WIDTH, PHOTO_HEIGHT),
          rotation: 1.5,
          settings: { illumination: 0, contrast: 0, sharpness: 0, noise: 0 },
        });
        const figures = { width: preview.width, height: preview.height };
        preview.release();
        return figures;
      },
    },
    {
      name: 'colour-correction',
      async run(context) {
        const jpeg = await context.engine.exportJpeg({
          corners: context.corners ?? defaultCorners(PHOTO_WIDTH, PHOTO_HEIGHT),
          rotation: 1.5,
          settings: DEFAULT_ENHANCEMENTS,
        });
        return { kilobytes: Math.round((jpeg.length * 3) / 4 / 1024) };
      },
    },
  ],
};

/** A grey table with a slightly skewed white page and lines of «text», like a phone photo. */
function drawDocumentPhoto(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) throw new RangeError('no 2d context: out of memory');
  context.fillStyle = '#6b6f76';
  context.fillRect(0, 0, width, height);
  context.fillStyle = '#f4f2ec';
  context.beginPath();
  context.moveTo(width * 0.14, height * 0.1);
  context.lineTo(width * 0.88, height * 0.13);
  context.lineTo(width * 0.84, height * 0.9);
  context.lineTo(width * 0.1, height * 0.86);
  context.closePath();
  context.fill();
  context.fillStyle = '#2b2b2b';
  for (let line = 0; line < 40; line += 1) {
    context.fillRect(
      width * 0.2,
      height * (0.16 + line * 0.017),
      width * (0.4 + (line % 5) * 0.05),
      height * 0.006,
    );
  }
  return canvas;
}

// ---- Elaborazione PDF: the PDF tool's engine on a 20-page scan ----

const PDF_PAGES = 20;

interface PdfContext {
  readonly engine: PdfEngine;
  file?: Blob;
  id?: number;
  stamp?: ArrayBuffer;
}

const PDF_TEST: TestDef<PdfContext> = {
  kind: 'pdf-engine',
  setUp: () => ({ engine: createPdfEngine() }),
  tearDown: (context) => context.engine.dispose(),
  steps: [
    {
      name: 'pdf-prepare',
      async run(context) {
        const { file, stamp } = await scannedPdf(PDF_PAGES);
        context.file = file;
        context.stamp = stamp;
        return { pages: PDF_PAGES, kilobytes: Math.round(file.size / 1024) };
      },
    },
    {
      name: 'pdf-open',
      async run(context) {
        const opened = await context.engine.open(need(context.file), 'prova.pdf');
        context.id = opened.id;
        return { pages: opened.info.pages };
      },
    },
    {
      name: 'pdf-merge',
      async run(context) {
        const id = need(context.id);
        const plan = [...Array(PDF_PAGES * 2).keys()].map((i) => ({
          source: id,
          page: i % PDF_PAGES,
        }));
        const merged = await context.engine.assemble(plan);
        return { pages: plan.length, kilobytes: Math.round(merged.size / 1024) };
      },
    },
    {
      name: 'pdf-place-image',
      async run(context) {
        const placed = await context.engine.assemble([
          {
            source: need(context.id),
            page: 0,
            images: [
              {
                bytes: need(context.stamp),
                type: 'jpeg',
                x: 72,
                y: 72,
                width: 200,
                height: 120,
                rotation: 15,
              },
            ],
          },
        ]);
        return { kilobytes: Math.round(placed.size / 1024) };
      },
    },
    {
      name: 'pdf-thumbnails',
      async run(context) {
        const thumbnails = await openThumbnails(need(context.file));
        const canvas = document.createElement('canvas');
        try {
          for (let page = 0; page < 6; page += 1) await thumbnails.render(page, canvas, 120);
        } finally {
          releaseCanvas(canvas);
          await thumbnails.destroy();
        }
        return { rendered: 6 };
      },
    },
  ],
};

function need<T>(value: T | undefined): T {
  if (value === undefined) throw new Error('an earlier step did not finish');
  return value;
}

/** A PDF of `pages` A4 pages, each a JPEG «scan», built in the browser with the same library. */
async function scannedPdf(pages: number): Promise<{ file: Blob; stamp: ArrayBuffer }> {
  const { PDFDocument } = await import('@cantoo/pdf-lib');
  const scan = drawDocumentPhoto(1240, 1754);
  const scanBytes = await canvasJpeg(scan, 0.8);
  releaseCanvas(scan);
  const doc = await PDFDocument.create();
  const image = await doc.embedJpg(scanBytes);
  for (let i = 0; i < pages; i += 1) {
    doc.addPage([595, 842]).drawImage(image, { x: 0, y: 0, width: 595, height: 842 });
  }
  const bytes = await doc.save();
  const stampCanvas = drawDocumentPhoto(400, 240);
  const stamp = await canvasJpeg(stampCanvas, 0.8);
  releaseCanvas(stampCanvas);
  return { file: new Blob([bytes], { type: 'application/pdf' }), stamp };
}

function canvasJpeg(canvas: HTMLCanvasElement, quality: number): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? void blob.arrayBuffer().then(resolve, reject)
          : reject(new RangeError('toBlob failed: out of memory')),
      'image/jpeg',
      quality,
    );
  });
}
